import React, { PureComponent, Children } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import Swipeable from 'react-swipeable';
import { withAppState } from '@ynap/immutables-provider';

const bem = new BEMHelper('Carousel');

const componentPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.func]);

export class Carousel extends PureComponent {
    static propTypes = {
        defaultIndex: PropTypes.number,
        slidesToShow: PropTypes.number,
        slidesToScroll: PropTypes.number,
        transition: PropTypes.string,
        prevComponent: componentPropType,
        nextComponent: componentPropType,
        pageComponent: componentPropType,
        classNames: PropTypes.shape({
            prev: PropTypes.string,
            next: PropTypes.string,
            container: PropTypes.string,
            trackClass: PropTypes.string,
            trackModifier: PropTypes.string,
            slideModifier: PropTypes.string,
            slide: PropTypes.string,
            slideClass: PropTypes.string,
            controlsModifier: PropTypes.string,
            controlsClass: PropTypes.string,
        }),
        children: PropTypes.node,
        onNextClick: PropTypes.func,
        onPrevClick: PropTypes.func,
        appState: PropTypes.shape({
            language: PropTypes.string,
            dir: PropTypes.string,
        }),
        useDefaultCarouselIndex: PropTypes.bool,
        offset: PropTypes.number
    };

    static defaultProps = {
        defaultIndex: 0,
        slidesToShow: 1,
        slidesToScroll: 1,
        prevComponent: 'button',
        nextComponent: 'button',
        pageComponent: 'button',
        transition: 'transform 0.3s ease-out',
        appState: {
            language: '',
            dir: 'ltr',
        },
        useDefaultCarouselIndex: true,
    };

    constructor(props) {
        super(props);
        const { defaultIndex, children } = this.props;
        const currentIndex = defaultIndex;
        const slides = Children.toArray(children);
        this.state = { slides, currentIndex, delta: 0, direction: 'right' };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { useDefaultCarouselIndex } = this.props;
        const { children } = nextProps;
        const slides = Children.toArray(children);
        this.setState({ slides, ...(useDefaultCarouselIndex ? { currentIndex: nextProps.defaultIndex } : {}) });
    }

    slideTo(index) {
        const {
            slidesToShow,
            appState: { dir },
        } = this.props;
        const { slides } = this.state;
        const currentIndex = Math.max(0, Math.min(index, slides.length - slidesToShow));
        const isRTL = dir === 'rtl';

        if (currentIndex === 0) {
            const direction = isRTL ? 'left' : 'right';
            this.setState({ currentIndex, delta: 0, direction });
        } else if (currentIndex === slides.length - slidesToShow) {
            const direction = isRTL ? 'right' : 'left';
            this.setState({ currentIndex, delta: 0, direction });
        } else {
            const direction = isRTL ? 'right left' : 'left right';
            this.setState({ currentIndex, delta: 0, direction });
        }
    }

    prev() {
        const { slidesToScroll, onPrevClick } = this.props;
        const { currentIndex } = this.state;
        onPrevClick && onPrevClick(currentIndex);
        this.slideTo(Math.ceil(currentIndex - slidesToScroll));
    }

    next() {
        const { slidesToScroll, onNextClick } = this.props;
        const { currentIndex } = this.state;
        onNextClick && onNextClick(currentIndex);
        this.slideTo(Math.floor(currentIndex + slidesToScroll));
    }

    handlePrevClick = () => {
        this.prev();
    };

    handleNextClick = () => {
        this.next();
    };

    handleSwipingLeft = /* istanbul ignore next */ (event, delta) => {
        if (event.cancelable) {
            event.preventDefault();
        }
        const state = {
            delta: -addResistance(delta),
            trackWidth: this.trackRef.offsetWidth,
        };

        this.setState(state);
    };

    handleSwipingRight = /* istanbul ignore next */ (event, delta) => {
        if (event.cancelable) {
            event.preventDefault();
        }
        this.setState({
            delta: addResistance(delta),
            trackWidth: this.trackRef.offsetWidth,
        });
    };

    handleSwiped = /* istanbul ignore next */ (event, deltaX, deltaY, isFlick, velocity) => {
        const { slidesToShow, slidesToScroll, appState } = this.props;
        const { currentIndex } = this.state;
        let nextIndex = currentIndex;

        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            const shiftIndex = slidesToShow % 1 === 0 ? slidesToScroll : Math.min(slidesToScroll, Math.ceil(velocity * slidesToScroll));
            const direction = appState.dir === 'rtl' ? 1 : -1;
            if (deltaX < 0) {
                nextIndex = Math.ceil(nextIndex + shiftIndex * direction);
            } else if (deltaX > 0) {
                nextIndex = Math.floor(nextIndex - shiftIndex * direction);
            }
        }

        return this.slideTo(nextIndex);
    };

    render() {
        const {
            slidesToShow,
            slidesToScroll,
            transition,
            prevComponent: PrevComponent,
            nextComponent: NextComponent,
            pageComponent: PageComponent,
            classNames = {},
            appState,
            offset = 0
        } = this.props;
        const { currentIndex, slides, delta, trackWidth, direction } = this.state;

        const { prev = null, next = null, container, trackModifier, slideModifier, slideClass, trackClass, viewportClass, controlsModifier, controlsClass } = classNames;

        const isLastWindow = currentIndex >= slides.length - slidesToShow
        const isRTL = appState.dir === 'rtl'
        const translatePercentage = (100 / slides.length) * currentIndex * (isRTL ? 1 : -1)
        // The offset is to prevent the last slide right padding from being displayed (left padding in case of rtl)
        const translateValueWithOffset = offset && isLastWindow ? `calc(${translatePercentage}% ${isRTL ? '-' : '+'} ${offset}px)` : `${translatePercentage}%`
        const trackStyle = {
            width: `${(slides.length / slidesToShow) * 100}%`,
            transition: delta === 0 ? transition : 'none',
            transform:
                isRTL
                    ? delta === 0
                        ? `translateX(${translateValueWithOffset})`
                        : `translateX(${delta + (trackWidth / slides.length) * currentIndex}px)`
                    : delta === 0
                      ? `translateX(${translateValueWithOffset})`
                      : `translateX(${delta - (trackWidth / slides.length) * currentIndex}px)`,
        };
        const slideStyle = { width: `${100 / slides.length}%` };

        const numPages = Math.ceil(slides.length / slidesToScroll);

        return (
            <div className={bem(null, null, [container, direction])} aria-roledescription="carousel" ref={this.containerRef}>
                <div className={bem('controls', controlsModifier, controlsClass)} role="group">
                    {PrevComponent && (
                        <PrevComponent
                            type="button"
                            className={bem('control', ['prev', controlsModifier])}
                            disabled={currentIndex <= 0}
                            aria-disabled={currentIndex <= 0}
                            onClick={this.handlePrevClick}
                            aria-label="Previous slide"
                        >
                            <i className={bem('arrow', ['prev', controlsModifier], prev)} aria-hidden="true"></i>
                        </PrevComponent>
                    )}
                    {NextComponent && (
                        <NextComponent
                            type="button"
                            className={bem('control', ['next', controlsModifier])}
                            disabled={isLastWindow}
                            aria-disabled={isLastWindow}
                            onClick={this.handleNextClick}
                            aria-label="Next slide"
                        >
                            <i className={bem('arrow', ['next', controlsModifier], next)} aria-hidden="true"></i>
                        </NextComponent>
                    )}
                </div>
                <Swipeable
                    className={bem('viewport', null, viewportClass)}
                    onSwipingLeft={this.handleSwipingLeft}
                    onSwipingRight={this.handleSwipingRight}
                    onSwiped={this.handleSwiped}
                >
                    <ul
                        className={bem('track', trackModifier, trackClass)}
                        ref={(node) => {
                            this.trackRef = node;
                        }}
                        style={trackStyle}
                        role="group"
                    >
                        {slides.map((slide, i) => (
                            <li key={i} className={bem('slide', slideModifier, slideClass)} style={slideStyle} aria-roledescription="slide">
                                {slide}
                            </li>
                        ))}
                    </ul>
                </Swipeable>
                {PageComponent && (
                    <ul className={bem('pagination')} role="group">
                        {Array(numPages)
                            .fill(undefined)
                            .map((_, i) => (
                                <li key={i}>
                                    <PageComponent
                                        className={bem('page')}
                                        type="button"
                                        disabled={i === Math.ceil(currentIndex / slidesToScroll)}
                                        onClick={() => this.slideTo(i * slidesToScroll)}
                                    >
                                        {i + 1}
                                    </PageComponent>
                                </li>
                            ))}
                    </ul>
                )}
            </div>
        );
    }
}

/* istanbul ignore next */
function addResistance(delta) {
    return delta * (1 - parseInt(Math.sqrt(Math.pow(delta, 2)), 10) / 1000);
}

export default withAppState()(Carousel);
